import {
  isForbiddenError,
  isNotFoundError,
  isSuccess,
} from '@/context/services/type';
import { useCheckInvoiceReportsProtectedResources } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportsProtectedResources';
import { isCommonError } from '@/libs/typeguard/isError';
import { INVOICE_REPORT_SUMMARY_QUERY_KEY } from '@/features/invoice_reports/constants';
import {
  extractInvoiceReportErrorMessage,
  getStatusCodeFromError,
} from '@/features/invoice_reports/utils/extractErrorMessage';
import { useTranslation } from '@/i18n';
import {
  GetInvoiceReportSummaryResponse,
  useGetInvoiceReportSummary,
} from 'ap-openapi';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

const TOAST_DURATION = 5;

type InvoiceReportSummaryError = {
  messages?: string[];
  code?: string;
};

function pickInvoiceReportSummaryErrorFields(
  err: unknown
): InvoiceReportSummaryError {
  if (err === null || typeof err !== 'object') {
    return {};
  }
  let messages: string[] | undefined;
  if ('messages' in err) {
    const m = err['messages'];
    if (
      Array.isArray(m) &&
      m.every((x): x is string => typeof x === 'string')
    ) {
      messages = m;
    }
  }
  let code: string | undefined;
  if ('code' in err && typeof err['code'] === 'string') {
    code = err['code'];
  }
  return { messages, code };
}

type UseInvoiceReportSummaryDataResult = {
  data?: GetInvoiceReportSummaryResponse;
  error?: InvoiceReportSummaryError;
  isLoading: boolean;
};

export const useInvoiceReportSummaryData = (
  reportId: string
): UseInvoiceReportSummaryDataResult => {
  const { data, error, isLoading } = useGetInvoiceReportSummary(reportId, {
    query: {
      queryKey: [INVOICE_REPORT_SUMMARY_QUERY_KEY, reportId],
    },
  });

  const body = data?.data;
  const status = data?.status;
  const isSuccessStatus = isSuccess(status);

  return useMemo(() => {
    if (isLoading) {
      return { isLoading: true };
    }

    if (error) {
      const statusFromHttp = getStatusCodeFromError(error);
      const { messages, code } = pickInvoiceReportSummaryErrorFields(error);
      return {
        error: {
          messages,
          code:
            code ??
            (statusFromHttp !== undefined ? String(statusFromHttp) : undefined),
        },
        isLoading: false,
      };
    }

    if (!isSuccessStatus) {
      return {
        error: {
          messages:
            typeof body === 'object' && isCommonError(body)
              ? body.messages
              : undefined,
          code: status?.toString(),
        },
        isLoading: false,
      };
    }

    if (body === undefined) {
      return {
        error: {
          messages: undefined,
          code: status?.toString(),
        },
        isLoading: false,
      };
    }

    return {
      data: body satisfies GetInvoiceReportSummaryResponse,
      isLoading: false,
    };
  }, [body, status, isSuccessStatus, isLoading, error]);
};

function getParsedSummaryErrorStatusCode(
  error: InvoiceReportSummaryError | undefined
): number | undefined {
  if (error?.code === undefined || error.code === '') return undefined;
  const n = Number.parseInt(error.code, 10);
  return Number.isFinite(n) ? n : undefined;
}

type SummaryAccessErrorEffectParams = {
  error?: InvoiceReportSummaryError;
  onClose: () => void;
  /**
   * When true, suppresses the error toast on 403 (used when a Rails flash alert
   * was already displayed on mount — e.g. after a redirect from a failed edit/detail
   * HTML request — to avoid showing two conflicting permission-error toasts).
   */
  suppressForbiddenToast?: boolean;
};

/**
 * 403: refetch list + `handleCheckPermission`; on success close drawer + toast; on failure reload.
 */
const useInvoiceReportSummaryForbiddenAccessEffect = ({
  error,
  onClose,
  suppressForbiddenToast,
}: SummaryAccessErrorEffectParams): void => {
  const { handleCheckPermission } = useCheckInvoiceReportsProtectedResources();
  const queryClient = useQueryClient();
  const notify = useToastAPI();
  const { t } = useTranslation();

  useEffect(() => {
    const statusCode = getParsedSummaryErrorStatusCode(error);
    if (error === undefined || !isForbiddenError(statusCode)) return;

    void (async () => {
      try {
        const errorMessage = extractInvoiceReportErrorMessage(error, t);
        await handleCheckPermission({
          fnCheckPermission: async () => {
            await queryClient.refetchQueries({
              queryKey: ['invoiceReports'],
            });
            const cached = queryClient
              .getQueryCache()
              .findAll({ queryKey: ['invoiceReports'] });
            const first = cached[0];
            if (!first) {
              return undefined;
            }
            return { data: first.state.data };
          },
          onCheckSuccess: () => {
            onClose();
            if (!suppressForbiddenToast) {
              notify.error({
                title:
                  errorMessage ??
                  error.messages?.join?.('\n') ??
                  t('system_error'),
                duration: TOAST_DURATION,
              });
            }
          },
          onCheckFailure: () => {
            location.reload();
          },
        });
      } catch {
        location.reload();
      }
    })();
  }, [
    error,
    onClose,
    suppressForbiddenToast,
    handleCheckPermission,
    queryClient,
    notify,
    t,
  ]);
};

/**
 * 404: error toast, close drawer, invalidate list query.
 */
const useInvoiceReportSummaryNotFoundAccessEffect = ({
  error,
  onClose,
}: SummaryAccessErrorEffectParams): void => {
  const queryClient = useQueryClient();
  const notify = useToastAPI();
  const { t } = useTranslation();

  useEffect(() => {
    const statusCode = getParsedSummaryErrorStatusCode(error);
    if (error === undefined || !isNotFoundError(statusCode)) return;

    notify.error({
      title: error.messages?.join?.('\n') || t('system_error'),
      duration: TOAST_DURATION,
    });
    onClose();
    void queryClient.invalidateQueries({
      queryKey: ['invoiceReports'],
    });
  }, [error, onClose, queryClient, notify, t]);
};

/**
 * Invoice report list drawer access errors: 403 vs 404 are handled in separate effects.
 */
export const useInvoiceReportSummaryAccessErrorEffect = (
  params: SummaryAccessErrorEffectParams
): void => {
  useInvoiceReportSummaryForbiddenAccessEffect(params);
  useInvoiceReportSummaryNotFoundAccessEffect(params);
};
